import { CountryMilestoneDependency } from './model/milestones/CountryMilestoneDependency';
import { CountryMilestoneDependencyDelayType } from './model/milestones/CountryMilestoneDependencyDelayType';
import { DateUtil } from './DateUtil';
import moment from 'moment';
import { RichMilestone } from './services/milestones/RichMilestone';

const MAX_CALCULATION_ITERATIONS = 100;

export interface DependentMsSync {
  milestoneData: RichMilestone;
  nextDate: Date;
  nextDone: boolean;
}

export class MilestoneDependencyUtil {

  public static calculateDependencyMilestones(milestones: RichMilestone[]): DependentMsSync[] {
    const incDepMs = milestones.filter(ms => ms.hasIncomingDependencies);
    if (incDepMs.length <= 0) {
      return [];
    }
    return Object.values(this.calculateSyncMap(incDepMs, milestones));
  }

  private static calculateSyncMap(
    incDepMs: RichMilestone[],
    milestones: RichMilestone[],
  ): Record<string, DependentMsSync> {

    const idToMs: Record<string, RichMilestone> = {};
    for (const ms of milestones) {
      idToMs[ms.id] = ms;
    }
    const syncMap: Record<string, DependentMsSync> = {};
    for (const dependentMs of incDepMs) {
      syncMap[dependentMs.id] = {
        milestoneData: dependentMs,
        nextDate: null,
        nextDone: true,
      };
    }

    for (let iteration = 0; iteration < MAX_CALCULATION_ITERATIONS; iteration++) {
      let somethingChangedThisIteration = false;

      for (const dependentMs of incDepMs) {

        for (const dependency of dependentMs.incomingDependencies) {
          const prevDate = DateUtil.anyToDate(syncMap[dependentMs.id].nextDate);
          const depDate = DateUtil.anyToDate(syncMap[dependency.source.id]?.nextDate ?? dependency.source.date);
          const nextDate = this.calculateNewDate(dependency.dependency, prevDate, depDate);

          if (!DateUtil.isEqual(prevDate, nextDate)) {
            syncMap[dependentMs.id].nextDate = nextDate;
            somethingChangedThisIteration = true;
          }

          const prevDone = syncMap[dependentMs.id].nextDone;
          const nextDone = prevDone && (syncMap[dependency.source.id]?.nextDone ?? dependency.source.done);
          if (prevDone !== nextDone) {
            syncMap[dependentMs.id].nextDone = nextDone;
            somethingChangedThisIteration = true;
          }
        }

      }

      if (!somethingChangedThisIteration) {
        break;
      }

    }

    return syncMap;
  }

  private static calculateNewDate(dependency: CountryMilestoneDependency, currentDate: Date, dependencyDate: Date): Date {
    if (!dependencyDate) {
      return currentDate;
    }
    const lagDays = dependency.lagDays;
    switch (dependency.delayType) {
      case CountryMilestoneDependencyDelayType.IMMEDIATE: return dependencyDate;
      case CountryMilestoneDependencyDelayType.LAG_BY: {
        const nextDate = moment(dependencyDate).add(lagDays, 'days').toDate();
        if (!currentDate || DateUtil.compareDates(currentDate, nextDate) < 0) {
          return nextDate;
        }
        return currentDate;
      }
      case CountryMilestoneDependencyDelayType.LEAD_BY: {
        const nextDate = moment(dependencyDate).add(-lagDays, 'days').toDate();
        if (!currentDate || DateUtil.compareDates(currentDate, nextDate) > 0) {
          return nextDate;
        }
        return currentDate;
      }
    }
  }

}
